import type { ReactNode } from 'react';

import { entityName } from '../../lib/scene-doc.js';
import { useScene } from '../../state/scene.js';
import {
  OVERLAY_PRESETS,
  VIEW_MODES,
  useSceneView,
  type EditorViewInfo,
  type GridPlane,
  type OverlayId,
  type ViewAxis,
} from '../../state/scene-view.js';
import { IconButton, MenuButton, NumberField, PropRow, SelectField, Toggle, type MenuItem } from '../ui/controls.js';
import { Icon, type IconName } from '../ui/icons.js';
import './scene-overlays.css';

/**
 * Viewport overlay panels (Unity's Scene view Overlays): Tools, Grid and
 * Snap, View Options, the orientation gizmo and the selected-camera preview
 * frame. Each can be open, collapsed to its title or hidden; presets switch
 * them all at once (the Overlays menu in the scene toolbar).
 */

const OVERLAY_LABELS: Record<OverlayId, string> = {
  tools: 'Tools',
  snapping: 'Grid and Snap',
  viewOptions: 'View Options',
  orientation: 'Orientation',
  cameraPreview: 'Camera Preview',
};

function OverlayPanel({ id, icon, children }: { id: OverlayId; icon: IconName; children: ReactNode }) {
  const state = useSceneView((s) => s.overlays[id]);
  const setOverlay = useSceneView((s) => s.setOverlay);
  if (state === 'hidden') return null;
  const open = state === 'open';
  return (
    <section className={`scene-overlay is-${state}`} aria-label={OVERLAY_LABELS[id]}>
      <header className="scene-overlay-head">
        <button
          type="button"
          className="scene-overlay-toggle"
          title={open ? 'Collapse' : 'Expand'}
          aria-expanded={open}
          onClick={() => setOverlay(id, open ? 'collapsed' : 'open')}
        >
          <Icon name={icon} size={12} />
          <span>{OVERLAY_LABELS[id]}</span>
          <Icon name={open ? 'chevronUp' : 'chevronDown'} size={11} />
        </button>
        <button type="button" className="scene-overlay-close" title="Hide overlay" onClick={() => setOverlay(id, 'hidden')}>
          <Icon name="x" size={10} />
        </button>
      </header>
      {open && <div className="scene-overlay-body">{children}</div>}
    </section>
  );
}

function ToolsOverlay() {
  const tool = useScene((s) => s.tool);
  const space = useScene((s) => s.space);
  const pivot = useSceneView((s) => s.pivot);
  const s = useScene.getState();
  const v = useSceneView.getState();
  return (
    <OverlayPanel id="tools" icon="cursor">
      <div className="overlay-tools">
        <IconButton icon="cursor" title="Select" shortcut="Q" active={tool === 'select'} onClick={() => s.setTool('select')} />
        <IconButton icon="move" title="Move" shortcut="W" active={tool === 'move'} onClick={() => s.setTool('move')} />
        <IconButton icon="rotate" title="Rotate" shortcut="E" active={tool === 'rotate'} onClick={() => s.setTool('rotate')} />
        <IconButton icon="scale" title="Scale (centre cube: uniform)" shortcut="R" active={tool === 'scale'} onClick={() => s.setTool('scale')} />
        <span className="overlay-sep" />
        <IconButton
          icon={pivot === 'pivot' ? 'focus' : 'square'}
          title={pivot === 'pivot' ? 'Handle at the active object’s pivot' : 'Handle at the selection’s centre'}
          shortcut="Z"
          onClick={() => v.setPivot(pivot === 'pivot' ? 'center' : 'pivot')}
        />
        <IconButton
          icon={space === 'world' ? 'globe' : 'local'}
          title={space === 'world' ? 'Global handle rotation' : 'Local handle rotation'}
          shortcut="X"
          onClick={() => s.setSpace(space === 'world' ? 'local' : 'world')}
        />
      </div>
    </OverlayPanel>
  );
}

function SnapOverlay() {
  const snapOn = useScene((s) => s.snap);
  const snap = useSceneView((s) => s.snapSettings);
  const set = useSceneView((s) => s.setSnapSettings);
  return (
    <OverlayPanel id="snapping" icon="magnet">
      <div className="overlay-form">
        <PropRow label="Snap" hint="Ctrl inverts while dragging; Ctrl+G toggles">
          <Toggle checked={snapOn} onChange={(on) => useScene.getState().setSnap(on)} />
        </PropRow>
        <PropRow label="Move" hint="metres">
          <NumberField value={snap.translate} step={0.05} min={0} onChange={(translate) => set({ translate })} />
        </PropRow>
        <PropRow label="Rotate" hint="degrees">
          <NumberField value={snap.rotate} step={1} min={0} onChange={(rotate) => set({ rotate })} />
        </PropRow>
        <PropRow label="Scale" hint="factor step">
          <NumberField value={snap.scale} step={0.05} min={0} onChange={(scale) => set({ scale })} />
        </PropRow>
        <PropRow label="Absolute" hint="world moves land on multiples of the move step (grid snap)">
          <Toggle checked={snap.absolute} onChange={(absolute) => set({ absolute })} />
        </PropRow>
        <PropRow label="Grid">
          <Toggle checked={snap.grid} onChange={(grid) => set({ grid })} />
        </PropRow>
        <PropRow label="Grid size" hint="metres between minor lines">
          <NumberField value={snap.gridSize} step={0.25} min={0.01} onChange={(gridSize) => set({ gridSize })} />
        </PropRow>
        <PropRow label="Grid plane">
          <SelectField
            value={snap.gridPlane}
            options={[
              { value: 'xz', label: 'XZ (ground)' },
              { value: 'xy', label: 'XY (front)' },
              { value: 'yz', label: 'YZ (side)' },
            ]}
            onChange={(v) => set({ gridPlane: v as GridPlane })}
          />
        </PropRow>
        <PropRow label="Vertex snap" hint="always on; otherwise hold V while moving">
          <Toggle checked={snap.vertex} onChange={(vertex) => set({ vertex })} />
        </PropRow>
        <PropRow label="Surface snap" hint="always on; otherwise hold Ctrl+Shift while moving">
          <Toggle checked={snap.surface} onChange={(surface) => set({ surface })} />
        </PropRow>
        <PropRow label="Align to surface" hint="surface snapping also turns the up axis to the surface normal">
          <Toggle checked={snap.alignToSurface} onChange={(alignToSurface) => set({ alignToSurface })} />
        </PropRow>
        <PropRow label="Affect" hint="which tools snap">
          <div className="overlay-affect">
            <Toggle label="Move" checked={snap.affectTranslate} onChange={(affectTranslate) => set({ affectTranslate })} />
            <Toggle label="Rotate" checked={snap.affectRotate} onChange={(affectRotate) => set({ affectRotate })} />
            <Toggle label="Scale" checked={snap.affectScale} onChange={(affectScale) => set({ affectScale })} />
          </div>
        </PropRow>
        <button
          type="button"
          className="ghost small"
          title="Move the selection to the closest grid points (grid size)"
          onClick={() => useSceneView.getState().snapSelectionToGrid()}
        >
          <Icon name="grid" size={12} /> Move to closest grid point
        </button>
      </div>
    </OverlayPanel>
  );
}

function ViewOptionsOverlay() {
  const v = useSceneView();
  const speed = useScene((s) => s.cameraSpeed);
  const doc = useScene((s) => s.doc);
  const cameras = doc ? doc.entities.filter((e) => e.components && 'camera' in e.components) : [];
  return (
    <OverlayPanel id="viewOptions" icon="eye">
      <div className="overlay-form">
        <PropRow label="View mode">
          <SelectField value={v.viewMode} options={VIEW_MODES.map((m) => ({ value: m.id, label: m.label }))} onChange={v.setViewMode} />
        </PropRow>
        <PropRow label="Layout">
          <SelectField
            value={v.layout}
            options={[
              { value: 'single', label: '1 view' },
              { value: 'two', label: '2 views' },
              { value: 'four', label: '4 views' },
            ]}
            onChange={(l) => v.setLayout(l as typeof v.layout)}
          />
        </PropRow>
        <PropRow label="Camera preview" hint="picture of the selected camera">
          <Toggle checked={v.cameraPreview} onChange={v.setCameraPreview} />
        </PropRow>
        <PropRow label="Preview camera" hint="pin the preview to one camera">
          <SelectField
            value={v.previewCamera}
            options={[{ value: '', label: 'Selected camera' }, ...cameras.map((e) => ({ value: e.id, label: entityName(e) }))]}
            onChange={v.setPreviewCamera}
          />
        </PropRow>
        <PropRow label="Camera speed">
          <NumberField value={speed} step={0.1} min={0.01} max={100} onChange={(n) => useScene.getState().setCameraSpeed(n)} />
        </PropRow>
        <PropRow label="Field of view" hint="degrees">
          <NumberField value={v.fov} step={1} min={1} max={179} onChange={(fov) => v.setClip({ fov })} />
        </PropRow>
        <PropRow label="Near clip">
          <NumberField value={v.near} step={0.01} min={0.001} onChange={(near) => v.setClip({ near })} />
        </PropRow>
        <PropRow label="Far clip">
          <NumberField value={v.far} step={100} min={1} onChange={(far) => v.setClip({ far })} />
        </PropRow>
      </div>
    </OverlayPanel>
  );
}

const AXES: { axis: [number, number, number]; view: ViewAxis; label: string; color: string; positive: boolean }[] = [
  { axis: [1, 0, 0], view: 'right', label: 'X', color: 'var(--axis-x, #f05454)', positive: true },
  { axis: [0, 1, 0], view: 'top', label: 'Y', color: 'var(--axis-y, #6bd35a)', positive: true },
  { axis: [0, 0, 1], view: 'front', label: 'Z', color: 'var(--axis-z, #4f86ff)', positive: true },
  { axis: [-1, 0, 0], view: 'left', label: '', color: 'var(--axis-x, #f05454)', positive: false },
  { axis: [0, -1, 0], view: 'bottom', label: '', color: 'var(--axis-y, #6bd35a)', positive: false },
  { axis: [0, 0, -1], view: 'back', label: '', color: 'var(--axis-z, #4f86ff)', positive: false },
];

/** World axis `v` in view space for an orbit camera (yaw about Y, then pitch about X). */
export function viewSpace([x, y, z]: [number, number, number], yaw: number, pitch: number): [number, number, number] {
  const [cy, sy] = [Math.cos(-yaw), Math.sin(-yaw)];
  const [x1, z1] = [x * cy + z * sy, -x * sy + z * cy];
  const [cp, sp] = [Math.cos(-pitch), Math.sin(-pitch)];
  return [x1, y * cp - z1 * sp, y * sp + z1 * cp];
}

export function viewName(view: EditorViewInfo | undefined): string {
  if (!view) return 'Persp';
  if (view.axis) return view.axis[0]!.toUpperCase() + view.axis.slice(1);
  return view.ortho ? 'Ortho' : 'Persp';
}

function OrientationOverlay() {
  const views = useSceneView((s) => s.views);
  const active = useSceneView((s) => s.active);
  const setView = useSceneView((s) => s.setView);
  const view = views[active];
  const yaw = view?.yaw ?? 0.6;
  const pitch = view?.pitch ?? -0.45;
  const r = 26;
  const items = AXES.map((a) => {
    const [x, y, z] = viewSpace(a.axis, yaw, pitch);
    return { ...a, sx: 34 + x * r, sy: 34 - y * r, depth: z };
  }).sort((p, q) => p.depth - q.depth);
  return (
    <OverlayPanel id="orientation" icon="globe">
      <div className="overlay-orientation">
        <svg width={68} height={68} viewBox="0 0 68 68" role="group" aria-label="View orientation">
          {items.map((a) => (
            <g key={a.view} className="orient-axis" onClick={() => setView(a.view)}>
              <title>{`${a.view[0]!.toUpperCase()}${a.view.slice(1)} view`}</title>
              {a.positive && <line x1={34} y1={34} x2={a.sx} y2={a.sy} stroke={a.color} strokeWidth={2} />}
              <circle cx={a.sx} cy={a.sy} r={a.positive ? 7 : 5} fill={a.positive ? a.color : 'var(--panel)'} stroke={a.color} strokeWidth={1.5} />
              {a.label && (
                <text x={a.sx} y={a.sy + 3.5} textAnchor="middle" fontSize={9} fontWeight={700} fill="#111">
                  {a.label}
                </text>
              )}
            </g>
          ))}
          <g className="orient-axis" onClick={() => setView('toggle')}>
            <title>Toggle perspective / orthographic (Numpad 5)</title>
            <rect x={29} y={29} width={10} height={10} rx={2} fill="var(--muted)" />
          </g>
        </svg>
        <button type="button" className="orient-label" title="Toggle perspective / orthographic" onClick={() => setView('toggle')}>
          {viewName(view)}
        </button>
        <button type="button" className="orient-label" title="Free perspective view" onClick={() => setView('persp')}>
          Free
        </button>
        <button type="button" className="orient-label" title="Look through the selected camera (Numpad 0)" onClick={() => setView('camera')}>
          Camera
        </button>
      </div>
    </OverlayPanel>
  );
}

/** Frames and labels of split views (the engine draws the pictures). */
function ViewFrames() {
  const views = useSceneView((s) => s.views);
  const active = useSceneView((s) => s.active);
  const preview = useSceneView((s) => s.preview);
  const previewOverlay = useSceneView((s) => s.overlays.cameraPreview);
  const rect = ([x, y, w, h]: [number, number, number, number]) => ({
    left: `${x * 100}%`,
    bottom: `${y * 100}%`,
    width: `${w * 100}%`,
    height: `${h * 100}%`,
  });
  return (
    <>
      {views.length > 1 &&
        views.map((v, i) => (
          <div key={i} className={`view-frame${i === active ? ' is-active' : ''}`} style={rect(v.rect)}>
            <span className="view-frame-label">{viewName(v)}</span>
          </div>
        ))}
      {preview && previewOverlay !== 'hidden' && (
        <div className="camera-preview-frame" style={rect(preview.rect)}>
          <span className="view-frame-label">
            <Icon name="camera" size={11} /> {preview.id}
          </span>
        </div>
      )}
    </>
  );
}

/** Toolbar menu: show / hide overlays and presets. */
export function OverlaysMenu() {
  const overlays = useSceneView((s) => s.overlays);
  const preset = useSceneView((s) => s.overlayPreset);
  const items = (): MenuItem[] => [
    { id: 'h', label: 'Overlays', heading: true },
    ...(Object.keys(OVERLAY_LABELS) as OverlayId[]).map((id) => ({
      id,
      label: OVERLAY_LABELS[id],
      icon: (overlays[id] === 'hidden' ? 'eyeOff' : 'eye') as IconName,
      run: () => useSceneView.getState().setOverlay(id, overlays[id] === 'hidden' ? 'open' : 'hidden'),
    })),
    { id: 'sep', label: '', separator: true },
    { id: 'hp', label: 'Presets', heading: true },
    ...Object.keys(OVERLAY_PRESETS).map((name) => ({
      id: `preset-${name}`,
      label: name,
      icon: (preset === name ? 'check' : 'layers') as IconName,
      run: () => useSceneView.getState().applyPreset(name),
    })),
  ];
  return (
    <MenuButton className="ibtn" title="Overlays" items={items}>
      <Icon name="layers" size={15} />
    </MenuButton>
  );
}

/** Toolbar menu: view modes grouped like Unity's Draw Mode menu. */
export function ViewModeMenu() {
  const mode = useSceneView((s) => s.viewMode);
  const current = VIEW_MODES.find((m) => m.id === mode);
  const items = (): MenuItem[] => {
    const out: MenuItem[] = [];
    let group = '';
    for (const m of VIEW_MODES) {
      if (m.group !== group) {
        if (group) out.push({ id: `sep-${m.group}`, label: '', separator: true });
        out.push({ id: `h-${m.group}`, label: m.group, heading: true });
        group = m.group;
      }
      out.push({ id: m.id, label: m.label, icon: m.id === mode ? 'check' : undefined, run: () => useSceneView.getState().setViewMode(m.id) });
    }
    return out;
  };
  return (
    <MenuButton className="picker" title="View mode" items={items}>
      <Icon name={mode === 'wireframe' || mode === 'shadedWireframe' ? 'wireframe' : 'sphere'} size={13} />
      <span className="picker-label">{current?.label ?? mode}</span>
      <Icon name="chevronDown" size={12} />
    </MenuButton>
  );
}

/** Toolbar menu: split layouts and axis views. */
export function LayoutMenu() {
  const layout = useSceneView((s) => s.layout);
  const v = useSceneView.getState();
  const items = (): MenuItem[] => [
    { id: 'h', label: 'Layout', heading: true },
    { id: 'single', label: '1 view', icon: layout === 'single' ? 'check' : 'square', run: () => v.setLayout('single') },
    { id: 'two', label: '2 views', icon: layout === 'two' ? 'check' : 'panelRight', run: () => v.setLayout('two') },
    { id: 'four', label: '4 views', icon: layout === 'four' ? 'check' : 'grid', run: () => v.setLayout('four') },
    { id: 'sep', label: '', separator: true },
    { id: 'hv', label: 'Active view', heading: true },
    { id: 'top', label: 'Top', shortcut: 'Num 7', run: () => v.setView('top') },
    { id: 'front', label: 'Front', shortcut: 'Num 1', run: () => v.setView('front') },
    { id: 'right', label: 'Right', shortcut: 'Num 3', run: () => v.setView('right') },
    { id: 'bottom', label: 'Bottom', shortcut: 'Ctrl+Num 7', run: () => v.setView('bottom') },
    { id: 'back', label: 'Back', shortcut: 'Ctrl+Num 1', run: () => v.setView('back') },
    { id: 'left', label: 'Left', shortcut: 'Ctrl+Num 3', run: () => v.setView('left') },
    { id: 'toggle', label: 'Perspective / Ortho', shortcut: 'Num 5', run: () => v.setView('toggle') },
    { id: 'camera', label: 'Selected camera', shortcut: 'Num 0', run: () => v.setView('camera') },
    { id: 'persp', label: 'Free perspective', run: () => v.setView('persp') },
  ];
  return (
    <MenuButton className="ibtn" title="Views and layout" items={items}>
      <Icon name={layout === 'four' ? 'grid' : layout === 'two' ? 'panelRight' : 'square'} size={15} />
    </MenuButton>
  );
}

export function SceneOverlays() {
  return (
    <>
      <ViewFrames />
      <div className="scene-overlays scene-overlays-left">
        <ToolsOverlay />
        <SnapOverlay />
      </div>
      <div className="scene-overlays scene-overlays-right">
        <OrientationOverlay />
        <ViewOptionsOverlay />
      </div>
    </>
  );
}
